// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { StepComponentProps } from '../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';

/** Strip non-numeric chars for percentage, keep single dot */
function toRawPercent(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

interface StepChargesFeesProps extends StepComponentProps {
  policyTypeCategory?: string | null;
}

export function StepChargesFees({
  data,
  onFieldChange,
  pendingFiles,
  onAddFile,
  onRemoveFile,
  policyTypeCategory,
}: StepChargesFeesProps) {
  const handlePercentChange = (field: string, inputValue: string) => {
    const raw = toRawPercent(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
  };

  const inputClass =
    "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary";

  const percentWrapper =
    "flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary";

  const textareaClass =
    "w-full min-h-[120px] px-md py-[12px] rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal resize-vertical";

  const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";

  const isInvestment = policyTypeCategory?.toUpperCase() === 'INVESTMENT';

  // ========================================================================
  // Investment flow — only 3 percentage fields
  // ========================================================================
  if (isInvestment) {
    return (
      <div className="flex flex-col gap-lg">
        <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
          Charges &amp; fees
        </div>

        {/* Row 1: Fund Management Charge / Broker Trail | Commissions on policy or account */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Fund Management Charge/ Broker Trail <span className="text-[#E7000B]">*</span>
            </div>
            <div className={percentWrapper}>
              <input
                type="text"
                inputMode="decimal"
                value={(data.brokerAdvisoryAmc as string) || ''}
                onChange={(e) => handlePercentChange('brokerAdvisoryAmc', e.target.value)}
                className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
              />
              <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
            </div>
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Commissions on policy or account <span className="text-[#E7000B]">*</span>
            </div>
            <div className={percentWrapper}>
              <input
                type="text"
                inputMode="decimal"
                value={(data.commissionsOnPolicy as string) || ''}
                onChange={(e) => handlePercentChange('commissionsOnPolicy', e.target.value)}
                className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
              />
              <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
            </div>
          </div>
        </div>

        {/* Row 2: Allocation % of Contributions */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Allocation % of Contributions <span className="text-[#E7000B]">*</span>
            </div>
            <div className={percentWrapper}>
              <input
                type="text"
                inputMode="decimal"
                value={(data.allocationPercentContributions as string) || ''}
                onChange={(e) => handlePercentChange('allocationPercentContributions', e.target.value)}
                className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
              />
              <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
            </div>
          </div>
          <div className="flex-1" />
        </div>
      </div>
    );
  }

  // ========================================================================
  // Pension flow — full charges & fees layout
  // ========================================================================
  return (
    <div className="flex flex-col gap-lg">
      {/* Title */}
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Charges &amp; fees
      </div>

      {/* Row 1: Fund Management Charge / Broker Trail | Commissions on policy or account */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Fund Management Charge/ Broker Trail <span className="text-[#E7000B]">*</span>
          </div>
          <div className={percentWrapper}>
            <input
              type="text"
              inputMode="decimal"
              value={(data.brokerAdvisoryAmc as string) || ''}
              onChange={(e) => handlePercentChange('brokerAdvisoryAmc', e.target.value)}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
            <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
          </div>
        </div>
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Commissions on policy or account <span className="text-[#E7000B]">*</span>
          </div>
          <div className={percentWrapper}>
            <input
              type="text"
              inputMode="decimal"
              value={(data.commissionsOnPolicy as string) || ''}
              onChange={(e) => handlePercentChange('commissionsOnPolicy', e.target.value)}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
            <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
          </div>
        </div>
      </div>

      {/* Row 2: Allocation % of Contributions | Exit Charges / penalties Details */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Allocation % of Contributions <span className="text-[#E7000B]">*</span>
          </div>
          <div className={percentWrapper}>
            <input
              type="text"
              inputMode="decimal"
              value={(data.allocationPercentContributions as string) || ''}
              onChange={(e) => handlePercentChange('allocationPercentContributions', e.target.value)}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
            <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
          </div>
        </div>
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Exit Charges/ penalties Details <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            value={(data.exitChargesDetails as string) || ''}
            onChange={(e) => onFieldChange('exitChargesDetails', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {/* Row 3: Transfer out costs */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Transfer out costs <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            value={(data.transferOutCosts as string) || ''}
            onChange={(e) => onFieldChange('transferOutCosts', e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex-1" />
      </div>

      {/* ══════════════ Divider ══════════════ */}
      <div className="w-full h-[1px] bg-[#E1E1E1]" />

      {/* ══════════════ Other Charges & Details ══════════════ */}
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Other Charges &amp; Details
      </div>

      {/* Guaranteed Returns Details | Terminal Bonus Details */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Guaranteed Returns Details <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            value={(data.guaranteedReturnsDetails as string) || ''}
            onChange={(e) => onFieldChange('guaranteedReturnsDetails', e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>
            Terminal Bonus Details <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            value={(data.terminalBonusDetails as string) || ''}
            onChange={(e) => onFieldChange('terminalBonusDetails', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {/* Other Charge Details (textarea) */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Other Charge Details <span className="text-[#E7000B]">*</span>
        </div>
        <textarea
          placeholder="Other Charge Details"
          maxLength={2000}
          value={(data.otherChargeDetails as string) || ''}
          onChange={(e) => onFieldChange('otherChargeDetails', e.target.value)}
          className={textareaClass}
        />
      </div>

      {/* Upload Documents */}
      <FileUploadField
        fieldKey="chargesFeesDocUpload"
        files={pendingFiles?.get('chargesFeesDocUpload')}
        onAddFile={onAddFile}
        onRemoveFile={onRemoveFile}
      />
    </div>
  );
}
